// Awakening: the endgame form of a few final-stage species (the reference game's mega forms, its numbers and rules).
// Shared by the server, the creature page, the crafting bench, the Bestiary and the sims.
//
// - The forms are data (shared/awakening-data.ts) and real species (`<species>-awakened`, shared/species.ts): their
//   base stats replace the species' own, about +100 in total; elements may change; Talent, quality, Bloodline, Aura,
//   refine and stars apply on top as on any creature.
// - Each form adds one Awakened move to its line's kit: 600 power, its new primary element, 30 s cooldown
//   (shared/moves.ts AWAKENED_CD_MS). The attack side follows the stronger of its new Attack and Magic.
// - Awakening Shards drop from boss and Boss Ladder wins; ten make the Awakening Stone of the species you choose at
//   the crafting bench (no lottery, no coin fee). An Aura creature needs the Aura Awakening Stone, from ten Aura
//   Awakening Shards (half as common), and stays an Aura. Shards, stones and awakened creatures trade on the market;
//   none sells to a merchant. Diamonds buy none of it.
// - Irreversible. An Awakened creature never ascends or awakens again, isn't a catch (Bestiary, Weekly Catch), and no
//   hunt spawns one. It fights in every mode, PvP included.
//
// Drop rates. The reference pays a mega fragment on 0.5% × (p + 1) of legendary-boss wins, p the boss pair (0 for the
// first), and the shiny fragment at half that. Its boss token drops 1 in 2,000 kills in its first Outland; our keys
// drop 1 in 4,000 (CONFIG.bossTokenOneIn), so boss wins come half as often and each pays twice the reference's
// chance (SHARD_SCALE). Fenrir, Typhon and Hachimata are pairs 0-2; Boss Ladder step r continues at pair r + 2,
// capped at twice the reference's top rate (6.5%, the 13 pairs its manual hints at).
import { BOSSES, SPECIES } from './species.ts';
import { AURA_AWAKENING_SHARD, AWAKENING_SHARD, auraAwakeningStone, awakeningStone } from './items.ts';
import type { Rng } from './rng.ts';

export { AWAKENINGS, awakenedId } from './awakening-data.ts';
export { AURA_AWAKENING_SHARD, AWAKENING_SHARD, auraAwakeningStone, awakeningStone };

/** Shards per stone. */
export const SHARDS_PER_STONE = 10;
/** Our boss wins come half as often as the reference's (keys 1 in 4,000 kills against its 1 in 2,000), so twice its chance. */
export const SHARD_SCALE = 2;
/** The reference's chance per boss pair: 0.5% × (p + 1). */
const pairChance = (p: number) => 0.005 * (p + 1);
/** Highest Awakening Shard chance per win: twice the reference's top rate of 6.5%. */
export const SHARD_CHANCE_CAP = 0.13;
/** Aura Awakening Shards drop at this share of the plain shard's chance. */
export const AURA_SHARD_SHARE = 0.5;

const round4 = (x: number) => Math.round(x * 1e4) / 1e4;

/** Awakening Shard chance on a win against a boss: 1% Fenrir, 2% Typhon, 3% Hachimata. */
export function bossShardChance(bossId: string): number {
  const p = BOSSES.findIndex((b) => b.id === bossId);
  return p < 0 ? 0 : round4(Math.min(SHARD_CHANCE_CAP, SHARD_SCALE * pairChance(p)));
}

/** Awakening Shard chance on a Boss Ladder win at step r: 1% × (r + 3), at most 13%. */
export function ladderShardChance(r: number): number {
  return round4(Math.min(SHARD_CHANCE_CAP, SHARD_SCALE * pairChance(r + BOSSES.length - 1)));
}

/** Aura Awakening Shard chance from the plain one. */
export const auraShardChance = (plain: number) => round4(plain * AURA_SHARD_SHARE);

/** The shard drops of one win at this plain chance, rolled on the fight's rng (plain first, then aura). */
export function rollShards(chance: number, rng: Rng): { item: string; qty: number }[] {
  const out: { item: string; qty: number }[] = [];
  if (rng.chance(chance)) out.push({ item: AWAKENING_SHARD, qty: 1 });
  if (rng.chance(auraShardChance(chance))) out.push({ item: AURA_AWAKENING_SHARD, qty: 1 });
  return out;
}

/** True for an Awakened form. */
export const isAwakened = (species: string) => !!SPECIES[species]?.awakened;
/** The species an Awakened form came from, else the species itself. */
export const baseOfAwakened = (species: string) => SPECIES[species]?.awakenedFrom ?? species;
/** The stone a creature needs to awaken: the Aura Awakening Stone for an Aura, else the Awakening Stone. */
export function stoneFor(c: { species: string; rolls: { aura: boolean } }): string {
  return c.rolls.aura ? auraAwakeningStone(c.species) : awakeningStone(c.species);
}

/**
 * Whether this creature can awaken now, and why not. `bag` is the trainer's; pass none to check everything but the
 * stone.
 */
export function awakenCheck(c: { species: string; rolls: { aura: boolean }; listed?: boolean }, bag?: Record<string, number>): { ok: boolean; why: string; stone: string } {
  const sp = SPECIES[c.species];
  const stone = sp ? stoneFor(c) : '';
  if (!sp) return { ok: false, why: 'No such creature.', stone };
  if (sp.awakened) return { ok: false, why: `${sp.name} is already awakened.`, stone };
  if (!sp.awakensTo) return { ok: false, why: `${sp.name} has no Awakened form.`, stone };
  if (c.listed) return { ok: false, why: 'Listed for sale.', stone };
  if (bag && (bag[stone] ?? 0) < 1) return { ok: false, why: `Needs a ${c.rolls.aura ? 'Aura Awakening Stone' : 'Awakening Stone'} for ${sp.name}.`, stone };
  return { ok: true, why: '', stone };
}

/** The creature after awakening: its species becomes the Awakened form; everything else stays. */
export function applyAwakening<T extends { species: string }>(c: T): T {
  const to = SPECIES[c.species]?.awakensTo;
  if (to) c.species = to;
  return c;
}

/**
 * Art hook for the item icons: until the art thread draws `icon-<item>` for the shards and stones, the client shows
 * these existing icons with the gilt Awakened marker (client/assets.ts iconUrl). A shard borrows the Tablet Fragment;
 * a stone borrows the Rune Tablet of its Awakened form's primary element.
 */
export const AWAKENING_ICON_STANDIN: Record<string, string> = Object.fromEntries([
  [AWAKENING_SHARD, 'icon-tablet-fragment'], [AURA_AWAKENING_SHARD, 'icon-tablet-fragment'],
  ...Object.values(SPECIES).filter((s) => s.awakened).flatMap((s) => [awakeningStone(s.awakenedFrom!), auraAwakeningStone(s.awakenedFrom!)].map((id) => [id, `icon-rune-tablet-${s.elements[0]}`])),
]);
